import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Box, Preloader, useResponsive } from '@common-fe/common-fe';
import styled from 'styled-components';

import { FileImage } from '@/common/constants';
import routes from '@/common/routes';
import UploadFileErrorPopup from '@/components/controls/DropScan/UploadFileErrorPopup';
import useFilesInDocumentStore, {
  clearFilesInDocumentStore,
} from '@/components/controls/DropScan/useFilesInDocument.store';
import { StepperSidebar } from '@/components/elements';
import { Topbar, ZendeskMoving } from '@/modules/core/components';
import { FileUploadContent } from '@/modules/transaction/components/ReimburseMe/FileUpload/FileUploadContent';
import useDeleteDocumentOnTabClose from '@/modules/transaction/components/ReimburseMe/hooks/useDeleteDocumentOnTabClose';
import useSetFileFromDocumentStream from '@/modules/transaction/components/ReimburseMe/hooks/useSetFileFromDocumentStream';
import useClaimCreatingStore from '@/modules/transaction/components/ReimburseMe/store/useClaimCreating.store';
import useRemoveFileQuery from '@/modules/transaction/queries/useRemoveFile.query';
import theme from '@/styles/theme';

import { clearScannedDocument } from './FileScan/clearScannedDocument';
import { ParsedFileByOcr } from './FileScan/fileScan.types';
import { useFileUploadPopupStore } from './FileUploadPopup/store';
import useFilesStore from './store/useFiles.store';
import ClaimInfo from './ClaimInfo';
import FileScan from './FileScan';
import FileUpload from './FileUpload';
import Receipts from './Receipts';
import { BillPayStepPath, ReimburseMeStepPath, Steps } from './ReimburseMe.types';

const StyledBox = styled(Box)`
  flex: 1;
`;

interface Props {
  isBillPayMode?: boolean;
}

const ReimburseMe: React.FC<Props> = React.memo(({ isBillPayMode }) => {
  const { removeFile } = useRemoveFileQuery();
  const addFileButtonRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [step, setStep] = useState(Steps.DRAG_RECEIPTS);
  const [fileUploadError, setFileUploadError] = useState<FileError | undefined>();
  const [isFileUploadErrorModalVisible, setIsFileUploadErrorModalVisible] = useState(false);
  const [scannedDocument, setScannedDocument] = useState<ParsedFileByOcr | undefined>();
  const reUploadedOcrFile = useFileUploadPopupStore((store) => store.reUploadedOcrFile);
  const isScanningCanceled = useFileUploadPopupStore((store) => store.isScanningCanceled);
  const resetReUploadedFile = useFileUploadPopupStore((store) => store.handleReset);
  const setFiles = useFilesStore((state) => state.setFiles);
  const activeFile = useFilesStore((state) => state.activeFile);
  useSetFileFromDocumentStream({});
  const { t } = useTranslation();
  const OPTIONS = useMemo(
    () => [
      { key: 1, value: t('File Upload'), hidden: step > Steps.DRAG_RECEIPTS },
      { key: 2, value: t('File Upload'), hidden: step < Steps.FILE_UPLOAD || step >= Steps.FILE_SCAN },
      { key: 3, value: t('File Upload'), hidden: step < Steps.FILE_SCAN },
      { key: 4, value: t('Claim Info') },
    ],
    [t, step]
  );

  const history = useHistory();

  const handleSetStep = useCallback(
    (newStep: Steps) => {
      setStep(newStep);

      const stepRoute = isBillPayMode ? BillPayStepPath?.[newStep] : ReimburseMeStepPath?.[newStep];

      if (stepRoute) {
        history.replace(stepRoute);
      }
    },
    [history, isBillPayMode]
  );

  const handlePreviousStep = useCallback(() => {
    if (step === Steps.CLAIM_INFO) {
      handleSetStep(Steps.DRAG_RECEIPTS);
      setScannedDocument(undefined);
    } else {
      handleSetStep(step - 1);
    }
  }, [step, handleSetStep]);

  const handleUploadFiles = useCallback((values: FileImage[]) => {
    setFiles(values);

    if (values.length) {
      handleSetStep(Steps.FILE_UPLOAD);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavigateToFileUploadStep = useCallback(() => handleSetStep(Steps.FILE_UPLOAD), [handleSetStep]);

  const handleNextStep = useCallback(async () => {
    if (step === Steps.DRAG_RECEIPTS) {
      handleSetStep(Steps.CLAIM_INFO);
      return;
    }
    if (step === Steps.FILE_SCAN) {
      handleSetStep(Steps.CLAIM_INFO);
      return;
    }
    handleSetStep(Steps.FILE_SCAN);
  }, [step, handleSetStep]);

  const {
    values: { files, documentId },
    handleReset,
    handleSetValues,
  } = useFilesInDocumentStore();

  useEffect(() => {
    setIsMounted(true);
    return () => {
      handleReset();
      clearFilesInDocumentStore();
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const isCreatingClaim = useClaimCreatingStore((store) => store.state.isCreatingClaim);
  const isClaimCreated = useClaimCreatingStore((store) => store.state.isClaimCreated);
  useDeleteDocumentOnTabClose({ documentId, isCreatingClaim, isClaimCreated });
  const deleteFile = useCallback(
    (id: string) => {
      const isScannedFileDeleted = files[activeFile]?.id === id;
      if (isScannedFileDeleted) {
        const deletedScannedDocument = clearScannedDocument(scannedDocument);
        setScannedDocument(deletedScannedDocument);
      }
      handleSetValues({ files: files.filter((file) => file.id !== id) });
      removeFile(id);
    },
    [files, handleSetValues, removeFile, activeFile, scannedDocument]
  );

  const onFileUploadError = useCallback((error?: FileError) => {
    setFileUploadError(error);
    setIsFileUploadErrorModalVisible(true);
  }, []);

  const onCloseFileUploadError = useCallback(() => {
    setFileUploadError(undefined);
    setIsFileUploadErrorModalVisible(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    let timer: NodeJS.Timeout;
    if (reUploadedOcrFile && !isScanningCanceled) {
      setScannedDocument(reUploadedOcrFile);
      setSubLoading(true);
      timer = setTimeout(() => {
        setSubLoading(false);
      }, 500);
    }

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reUploadedOcrFile]);

  useEffect(() => () => resetReUploadedFile(), [resetReUploadedFile]);

  useLayoutEffect(() => {
    const stepRoute = isBillPayMode
      ? BillPayStepPath[Steps.DRAG_RECEIPTS]
      : ReimburseMeStepPath[Steps.DRAG_RECEIPTS];
    if (stepRoute) {
      history.replace(stepRoute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isMobile } = useResponsive();
  return (
    <Box direction="row" flex="grow" data-testid="Reimburse-me-wrapper">
      {!isMobile && (
        <StepperSidebar
          title={t(isBillPayMode ? 'Pay a Bill' : 'Reimburse Me')}
          options={OPTIONS}
          step={step}
        />
      )}

      <StyledBox
        direction="row"
        justify="center"
        height={{
          min: '100vh',
        }}
        style={{
          display: 'block',
          position: 'relative',
        }}
      >
        <StyledBox
          height={{
            min: '100%',
          }}
        >
          {!isMobile && (
            <Box direction="row" justify="center">
              <Box flex="grow" pad={{ horizontal: '40px' }} width={{ max: theme.defaultContentWidth }}>
                <Topbar />
              </Box>
            </Box>
          )}

          <Switch>
            <Route
              key={isBillPayMode ? routes.REQUEST_BILL_PAY : routes.REIMBURSE_ME}
              path={isBillPayMode ? routes.REQUEST_BILL_PAY : routes.REIMBURSE_ME}
              exact
            >
              {isMounted && (
                <Receipts
                  onSetFiles={handleUploadFiles}
                  onNext={handleNextStep}
                  onNavigateToFileUpload={handleNavigateToFileUploadStep}
                  isBillPayMode={isBillPayMode}
                  handleDeleteFile={deleteFile}
                  onFileUploadError={onFileUploadError}
                />
              )}
            </Route>

            <Route
              key={isBillPayMode ? routes.REQUEST_BILL_PAY_FILE_UPLOAD : routes.REIMBURSE_ME_FILE_UPLOAD}
              path={isBillPayMode ? routes.REQUEST_BILL_PAY_FILE_UPLOAD : routes.REIMBURSE_ME_FILE_UPLOAD}
              exact
            >
              <FileUpload
                onBack={handlePreviousStep}
                onNext={handleNextStep}
                handleDeleteFile={deleteFile}
                onFileUploadError={onFileUploadError}
                isBillPayMode={isBillPayMode}
              />
            </Route>

            <Route
              key={isBillPayMode ? routes.REQUEST_BILL_PAY_FILE_SCAN : routes.REIMBURSE_ME_FILE_SCAN}
              path={isBillPayMode ? routes.REQUEST_BILL_PAY_FILE_SCAN : routes.REIMBURSE_ME_FILE_SCAN}
              exact
            >
              <FileScan onNext={handleNextStep} onGetScannedDocument={setScannedDocument} />
            </Route>

            <Route
              key={isBillPayMode ? routes.REQUEST_BILL_PAY_CLAIM_INFO : routes.REIMBURSE_ME_CLAIM_INFO}
              path={isBillPayMode ? routes.REQUEST_BILL_PAY_CLAIM_INFO : routes.REIMBURSE_ME_CLAIM_INFO}
              exact
            >
              {subLoading ? (
                <Preloader />
              ) : (
                <ClaimInfo
                  onBack={handlePreviousStep}
                  scannedDocument={scannedDocument}
                  addFileButtonRef={addFileButtonRef}
                  isBillPayMode={isBillPayMode}
                >
                  <FileUploadContent
                    isSmallViewVersion
                    addFileButtonRef={addFileButtonRef}
                    noReceiptsTitle={t('There are no receipts uploaded')}
                    handleDeleteFile={deleteFile}
                    onFileUploadError={onFileUploadError}
                  />
                </ClaimInfo>
              )}
            </Route>
          </Switch>

          {isFileUploadErrorModalVisible && (
            <UploadFileErrorPopup fileError={fileUploadError} onClose={onCloseFileUploadError} />
          )}
        </StyledBox>
      </StyledBox>
      <ZendeskMoving translocation={500} />
    </Box>
  );
});

export default ReimburseMe;
